import { createRef, useCallback, useEffect, useState } from 'react';
import CustomForm from 'components/form/CustomForm';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import uuid from 'react-uuid';
import Form from '@rjsf/core';
import { SectionTitle } from 'components/form/SectionTitle';
import { AddFieldButton, RemoveFieldButton, SchemaPropertyCard } from './Form.styles';
import {
  LLMExtractionConfiguration,
  LLMExtractionSchemaPropertyTemp,
  LLMExtractionSchema,
  CreateWorkflowNode,
} from 'api/model';
import {
  NodeFormProps,
  schemaLLMExtractionSchemaProperty,
  uiSchemaLLMExtractionSchemaProperty,
} from 'api/workflows-steps/formSchema';
import { toast } from 'react-toastify';
import LLMCommonForm from './LLMCommonForm';
import { useNodeValidation } from 'hooks/useNodeValidation';

type SchemaExtractionProperty = {
  id: string;
  ref?: React.MutableRefObject<Form>;
  data?: LLMExtractionSchemaPropertyTemp;
};

const ExtractionSchemaForm = (props: NodeFormProps) => {
  const { initialValues = {} as CreateWorkflowNode } = props;
  const [formData, setFormData] = useState<CreateWorkflowNode>(initialValues);
  const [extractionSchemaProperty, setExtractionSchemaProperty] = useState<SchemaExtractionProperty[]>([]);

  const { validate } = useNodeValidation(initialValues.type);

  useEffect(() => {
    const init = initialValues;
    if (init.configuration) {
      const conf = init.configuration as LLMExtractionConfiguration;
      const buff: SchemaExtractionProperty[] = [];
      for (const p in conf.extraction_schema.properties) {
        const data = conf.extraction_schema.properties[p];
        buff.push({
          id: uuid(),
          ref: createRef() as React.MutableRefObject<Form>,
          data: {
            ...data,
            field_key: p,
            required: conf.extraction_schema.required.includes(p),
            is_enum: data.enum ? data.enum.length > 0 : undefined,
            array_items_type: data.items?.type,
          } as LLMExtractionSchemaPropertyTemp,
        });
      }
      setExtractionSchemaProperty(buff);
    }
  }, [initialValues]);

  const handleSubmit = useCallback(async () => {
    const isValid: boolean = extractionSchemaProperty.filter((p) => !p.ref?.current?.validateForm()).length == 0;

    const conf = formData.configuration as LLMExtractionConfiguration | undefined;
    if (
      !conf?.extraction_schema ||
      !conf.extraction_schema.properties ||
      Object.keys(conf.extraction_schema.properties).length === 0
    ) {
      toast.error("Extraction schema can't be empty! Add some fields.");
    } else if (isValid && (await validate(formData))) {
      props.onSubmit?.(formData);
    }
  }, [formData, props.onSubmit, extractionSchemaProperty]);

  const handleBaseFormOnChange = (formDataToChange: CreateWorkflowNode) => {
    const newFormData = {
      ...formDataToChange,
      configuration: {
        ...formDataToChange.configuration,
        extraction_schema: (formData.configuration as LLMExtractionConfiguration | undefined)?.extraction_schema,
      },
    };
    setFormData(newFormData);
    if (props.onChange) {
      props.onChange(newFormData);
    }
  };

  const handlePropertyOnChange = (tempFormData: LLMExtractionSchemaPropertyTemp, id: string) => {
    const property = extractionSchemaProperty.find((p) => p.id == id);
    if (property) {
      const index = extractionSchemaProperty.indexOf(property);
      if (index != -1) {
        extractionSchemaProperty[index] = {
          ...property,
          data: tempFormData,
        } as SchemaExtractionProperty;
      }
    }
    setExtractionSchemaProperty(extractionSchemaProperty);

    updateFormData(extractionSchemaProperty);
  };

  const updateFormData = (extractionProperties: SchemaExtractionProperty[]) => {
    const extractionSchema: LLMExtractionSchema = {
      properties: extractionProperties.reduce(
        (a, p) => ({
          ...a,
          [p.data?.field_key ?? '']: {
            type: p.data?.type,
            description: p.data?.description,
            enum: p.data?.enum,
            items: p.data?.array_items_type ? { type: p.data?.array_items_type } : undefined,
          },
        }),
        {},
      ),
      required:
        extractionProperties.filter((p) => p.data?.required && p.data?.field_key).map((p) => p.data!.field_key) ?? [],
    };

    setFormData({ ...formData, configuration: { ...formData.configuration, extraction_schema: extractionSchema } });
  };

  const handleAddExtractionSchemaProperty = () => {
    setExtractionSchemaProperty([
      ...extractionSchemaProperty,
      {
        id: uuid(),
        ref: createRef(),
      } as SchemaExtractionProperty,
    ]);
  };

  const handleDeleteExtractionSchemaProperty = (id: string) => {
    const properties = extractionSchemaProperty.filter((p) => p.id != id);
    setExtractionSchemaProperty(properties);
    updateFormData(properties);
  };

  return (
    <>
      <LLMCommonForm {...props} onSubmit={handleSubmit} onChange={handleBaseFormOnChange} validate={false} />
      <SectionTitle title="Extraction schema" />

      <div className="flex flex-col">
        {extractionSchemaProperty.map((p) => (
          <SchemaPropertyCard key={p.id}>
            <CustomForm<LLMExtractionSchemaPropertyTemp>
              schema={schemaLLMExtractionSchemaProperty}
              uiSchema={uiSchemaLLMExtractionSchemaProperty}
              onSubmit={handleSubmit}
              onChange={(data) => handlePropertyOnChange(data, p.id)}
              initialValues={p.data}
              formId={`${props.formId}-${p.id}`}
              ref={p.ref}
              className="w-full"
              disabled={props.disabled}
            />
            <div className="flex items-center pl-1">
              <RemoveFieldButton
                aria-label="delete"
                size="small"
                onClick={() => handleDeleteExtractionSchemaProperty(p.id)}
                disabled={props.disabled}
              >
                <DeleteIcon fontSize="small" />
              </RemoveFieldButton>
            </div>
          </SchemaPropertyCard>
        ))}
        <AddFieldButton
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddExtractionSchemaProperty}
          disabled={props.disabled}
        >
          Add extraction field
        </AddFieldButton>
      </div>
    </>
  );
};

export default ExtractionSchemaForm;
